import React, { useEffect, useState } from "react";
import { Check, X, Mail, ArrowRight, RefreshCw } from "lucide-react";

const Confirm = () => {
    const [status, setStatus] = useState("loading");
    const [countdown, setCountdown] = useState(3);

    // Mock navigation functions for demo
    const navigate = (path) => {
        console.log(`Navigating to: ${path}`);
        alert(`Would navigate to: ${path}`);
    };

    useEffect(() => {
        // Mock URL params for demo - in real app, use useSearchParams
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get("token") || "demo-token";

        if (!token) {
            setStatus("error");
            return;
        }

        const confirmEmail = async () => {
            try {
                // Simulate API call - replace with actual endpoint
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                // Mock response - replace with actual API call
                const mockSuccess = Math.random() > 0.3; // 70% success rate for demo
                
                if (mockSuccess) {
                    setStatus("success");
                    // Start countdown for redirect
                    const timer = setInterval(() => {
                        setCountdown((prev) => {
                            if (prev <= 1) {
                                clearInterval(timer);
                                navigate("/login");
                                return 0;
                            }
                            return prev - 1;
                        });
                    }, 1000);
                } else {
                    setStatus("error");
                }
            } catch (error) {
                console.error("Error confirming email:", error);
                setStatus("error");
            }
        };

        confirmEmail();
    }, []);

    const LoadingState = () => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Verifying Your Email</h1>
                    <p className="text-gray-600">Please wait while we confirm your email address...</p>
                </div>
                <div className="flex justify-center">
                    <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                </div>
            </div>
        </div>
    );

    const SuccessState = () => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center animate-fade-in">
                <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4 animate-scale-in">
                        <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Email Confirmed!</h1>
                    <p className="text-gray-600 mb-4">Your email has been successfully verified. Welcome aboard!</p>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center space-x-2 text-green-700">
                        <Mail className="w-5 h-5" />
                        <span className="text-sm font-medium">Account activated successfully</span>
                    </div>
                </div>

                <div className="text-center">
                    <p className="text-gray-600 mb-4">
                        Redirecting to login in <span className="font-bold text-green-600">{countdown}</span> seconds...
                    </p>
                    <button
                        onClick={() => navigate("/login")}
                        className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium"
                    >
                        Go to Login Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                </div>
            </div>
        </div>
    );

    const ErrorState = () => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-pink-100 p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center animate-fade-in">
                <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4 animate-shake">
                        <X className="w-8 h-8 text-red-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Verification Failed</h1>
                    <p className="text-gray-600 mb-4">We couldn't verify your email address. This could happen if:</p>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                    <ul className="text-sm text-red-700 space-y-1">
                        <li>• The confirmation link has expired</li>
                        <li>• The link has already been used</li>
                        <li>• The link is invalid or corrupted</li>
                    </ul>
                </div>

                <div className="space-y-3">
                    <button
                        onClick={() => navigate("/login")}
                        className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
                    >
                        Go to Login
                    </button>
                    <button
                        onClick={() => navigate("/register")}
                        className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                    >
                        Create New Account
                    </button>
                    <button
                        onClick={() => setStatus("loading")}
                        className="w-full px-6 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200 font-medium text-sm"
                    >
                        Try Demo Again
                    </button>
                </div>
            </div>
        </div>
    );

    if (status === "loading") return <LoadingState />;
    if (status === "success") return <SuccessState />;
    if (status === "error") return <ErrorState />;

    return null;
};

// Add custom CSS animations
const styles = `
@keyframes fade-in {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes scale-in {
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.animate-fade-in {
  animation: fade-in 0.6s ease-out;
}

.animate-scale-in {
  animation: scale-in 0.5s ease-out 0.2s both;
}

.animate-shake {
  animation: shake 0.6s ease-in-out;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default Confirm;
